import {useMemo, useRef} from 'react'
import type {Task} from '../types/task'
import {useQuery} from '@github-ui/react-query'
import type {IdToPullRequestMap} from '../types/pull'

export function getSortedResourceIds(tasks: Task[]): string {
  const ids = tasks ? tasks.map(task => task.resource_id) : []
  // Dedupe before sorting
  const uniqueIds = Array.from(new Set(ids))
  return uniqueIds.sort((a, b) => Number(a) - Number(b)).join(',')
}

export function usePullRequests({
  tasks,
  sessionsPollingInterval = 3000,
  initialPullRequestsMap = {},
}: {
  tasks: Task[]
  sessionsPollingInterval?: number | false
  initialPullRequestsMap?: IdToPullRequestMap
}) {
  const pullRequestIds = getSortedResourceIds(tasks)

  // The ref will hold our cumulative data to prevent flicker
  const cumulativePullsRef = useRef<IdToPullRequestMap>(initialPullRequestsMap)

  const query = useQuery<IdToPullRequestMap>({
    queryKey: ['agent-sessions-pull-requests', pullRequestIds],
    queryFn: async () => {
      const response = await fetch(
        `/copilot/agent-sessions/pull-requests?pull_request_ids=${encodeURIComponent(pullRequestIds)}`,
        {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        },
      )

      if (!response.ok) {
        throw new Error('Failed to fetch pull requests')
      }

      const data = await response.json()
      return data.pull_requests
    },
    refetchInterval: sessionsPollingInterval,
    refetchIntervalInBackground: false,
    enabled: pullRequestIds.length > 0,
  })

  // When new data comes in, merge it into our cumulative ref
  if (query.data) {
    cumulativePullsRef.current = {
      ...cumulativePullsRef.current,
      ...query.data,
    }
  }

  // We use a memo to ensure our component re-renders when the query is done,
  // returning the complete, cumulative data from the ref.
  const finalMap = useMemo(() => {
    // This memo depends on the query's data. When the fetch completes,
    // query.data changes, this recalculates, and the component re-renders
    // with the full map from the ref.
    return cumulativePullsRef.current
    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data])

  return {
    ...query,
    // Always return the cumulative data to prevent the UI from ever emptying out in transitional states
    data: finalMap,
  }
}
